import LocationOnIcon from '@mui/icons-material/LocationOn';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import PersonIcon from '@mui/icons-material/Person';
import PhoneIcon from '@mui/icons-material/Phone';
import TuneIcon from '@mui/icons-material/Tune';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import InputAdornment from '@mui/material/InputAdornment';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Snackbar from '@mui/material/Snackbar';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { type FC, useEffect, useState } from 'react';
import { useBciGetCommunesQuery, useBciGetRegionsQuery, useBciQuoteMutation } from 'services/api/bciSeguros.api';
import type { BciSegurosFormData } from 'services/types/bciSegurosForm';
import { normalizeChileanPhone, parseRut } from 'utils/bciSegurosFormsHelpers';

import {
  backButtonSx,
  NAVY,
  primaryButtonSx,
  sectionIconSx,
  sectionSx,
  sectionTitleSx,
  YELLOW,
} from '../styleConstants';

const BCI_PRODUCTS = [
  { code: 22002431, name: 'FULL VP', validity: 12, newOnly: true },
  { code: 22002432, name: 'FULL VP', validity: 24, newOnly: true },
  { code: 22002429, name: 'LIGHT VP', validity: 12, newOnly: true },
  { code: 22002430, name: 'LIGHT VP', validity: 24, newOnly: true },
  { code: 22002427, name: 'USADOS GARANTÍA', validity: 12, usedOnly: true },
  { code: 22002428, name: 'USADOS GARANTÍA', validity: 24, usedOnly: true },
] as const;

interface Props {
  formData: BciSegurosFormData;
  onFormDataChange: (updates: Partial<BciSegurosFormData>) => void;
  onNext: () => void;
  onBack: () => void;
  setIsLoading: (loading: boolean) => void;
  businessUnitId: number;
}

const ContractorAndConfigStep: FC<Props> = ({
  formData,
  onFormDataChange,
  onNext,
  onBack,
  setIsLoading,
  businessUnitId,
}) => {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [quoteError, setQuoteError] = useState<string | null>(null);

  const { data: regions = [] } = useBciGetRegionsQuery();
  const { data: communes = [] } = useBciGetCommunesQuery(
    { regionId: formData.contractor.regionId || 0 },
    { skip: !formData.contractor.regionId }
  );
  const [quote] = useBciQuoteMutation();

  const updateContractor = (updates: Partial<BciSegurosFormData['contractor']>) => {
    onFormDataChange({ contractor: { ...formData.contractor, ...updates } });
  };

  const updateConfig = (updates: Partial<BciSegurosFormData['insuranceConfig']>) => {
    onFormDataChange({ insuranceConfig: { ...formData.insuranceConfig, ...updates } });
  };

  const isSame = formData.contractor.isSameAsInsured;

  useEffect(() => {
    if (isSame) {
      updateContractor({
        rut: formData.insured.rut,
        dv: formData.insured.dv,
        rutFormatted: formData.insured.rutFormatted,
        firstName: formData.insured.firstName,
        lastName: formData.insured.lastName,
        secondLastName: formData.insured.secondLastName,
        phone: formData.insured.phone,
        email: formData.insured.email,
      });
    }
  }, [isSame, formData.insured]);

  const handleSubmit = async () => {
    const newErrors: Record<string, string> = {};
    if (!formData.contractor.rut) newErrors.rut = 'Requerido';
    if (!formData.contractor.firstName.trim()) newErrors.firstName = 'Requerido';
    if (!formData.contractor.lastName.trim()) newErrors.lastName = 'Requerido';
    if (!formData.contractor.regionId) newErrors.region = 'Requerida';
    if (!formData.contractor.communeId) newErrors.commune = 'Requerida';
    if (!formData.contractor.address.trim()) newErrors.address = 'Requerida';

    setErrors(newErrors);
    if (Object.values(newErrors).some(Boolean)) return;

    // Run quote
    setIsLoading(true);
    setQuoteError(null);
    try {
      const vehicle = {
        Int_Marca: formData.vehicle.brandId,
        Int_Modelo: formData.vehicle.modelId,
        Int_Ano: formData.vehicle.year,
        Int_TipoVehiculo: formData.vehicle.vehicleType,
        Int_UsoVehiculo: formData.vehicle.vehicleUsage,
        ...(formData.vehicle.licensePlate ? { Str_Patente: formData.vehicle.licensePlate } : {}),
        Propietario: {
          Int_Rut: formData.insured.rut,
          Str_Dv: formData.insured.dv,
          Int_Edad: formData.insured.age,
        },
      };

      const isUsed = formData.vehicle.vehicleType === 1;
      const applicableProducts = BCI_PRODUCTS.filter((p) => {
        if (p.validity !== formData.insuranceConfig.validityPeriod) return false;
        if ('usedOnly' in p && p.usedOnly && !isUsed) return false;
        if ('newOnly' in p && p.newOnly && isUsed) return false;
        return true;
      });

      const requestBody = {
        Lst_Productos: applicableProducts.map((p) => ({
          int_CodigoProducto: p.code,
          Lst_Vehiculos: [vehicle],
          Int_CantidadCuotas: formData.insuranceConfig.installmentCount,
          Int_Vigencia: p.validity,
        })),
        Int_FormaPago: formData.insuranceConfig.paymentMethod,
        int_RutCliente: formData.insured.rut,
        str_DvCliente: formData.insured.dv,
        businessUnitId,
        clientInfo: {
          phone: formData.insured.phone,
          firstName: formData.insured.firstName,
          lastName: formData.insured.lastName,
          secondLastName: formData.insured.secondLastName || undefined,
          email: formData.insured.email,
          rut: formData.insured.rutFormatted,
          age: formData.insured.age,
        },
        vehicleInfo: {
          licensePlate: formData.vehicle.licensePlate || undefined,
          brand: formData.vehicle.brandName || undefined,
          model: formData.vehicle.modelName || undefined,
          year: formData.vehicle.year || undefined,
        },
      };

      const result = await quote(requestBody).unwrap();

      if (!result.lst_Productos || result.lst_Productos.length === 0) {
        setQuoteError('No fue posible cotizar este vehículo. Verifica los datos e intenta nuevamente.');
        return;
      }

      onFormDataChange({
        quoteResult: {
          ...formData.quoteResult,
          quoteNumber: result.str_NroCotizacion,
          products: result.lst_Productos,
          validityStartDate: result.dt_InicioVigencia,
          validityEndDate: result.dt_FinVigencia,
          selectedProductCode: null,
          selectedDeductibleId: null,
          leadId: result.leadId,
          clientId: result.clientId,
          quoteMapping: result.quotes,
        },
      });

      onNext();
    } catch (error) {
      console.error('Error en tarificación:', error);
      setQuoteError('Ocurrió un error al cotizar. Intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      {/* Toggle: Same as Insured */}
      <Box
        onClick={() => updateContractor({ isSameAsInsured: !isSame })}
        sx={{
          py: 1.5,
          px: 0,
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          mb: 1,
          '&:hover': { opacity: 0.8 },
        }}
      >
        <Checkbox checked={isSame} sx={{ color: NAVY, '&.Mui-checked': { color: YELLOW }, mr: 1 }} />
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600, color: NAVY }}>
            Contratante es el mismo que el asegurado
          </Typography>
          <Typography variant="caption" sx={{ color: NAVY, opacity: 0.5 }}>
            Se copiarán los datos del paso anterior
          </Typography>
        </Box>
      </Box>

      {/* Contractor data */}
      <Box sx={{ ...(sectionSx as object), ...(isSame ? { opacity: 0.55, pointerEvents: 'none' } : {}) }}>
        <Typography variant="subtitle2" sx={sectionTitleSx}>
          <PersonIcon sx={sectionIconSx} />
          Datos del contratante
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr 1fr' }, gap: 2 }}>
          <TextField
            label="RUT"
            value={formData.contractor.rutFormatted}
            onChange={(e) => {
              const { rut, dv } = parseRut(e.target.value);
              updateContractor({ rutFormatted: e.target.value, rut, dv });
            }}
            error={!!errors.rut}
            disabled={isSame}
            size="small"
          />
          <TextField
            label="Nombres"
            value={formData.contractor.firstName}
            onChange={(e) => updateContractor({ firstName: e.target.value })}
            error={!!errors.firstName}
            disabled={isSame}
            size="small"
          />
          <TextField
            label="Apellido Paterno"
            value={formData.contractor.lastName}
            onChange={(e) => updateContractor({ lastName: e.target.value })}
            error={!!errors.lastName}
            disabled={isSame}
            size="small"
          />
          <TextField
            label="Apellido Materno"
            value={formData.contractor.secondLastName}
            onChange={(e) => updateContractor({ secondLastName: e.target.value })}
            disabled={isSame}
            size="small"
          />
        </Box>
      </Box>

      <Box sx={{ ...(sectionSx as object), ...(isSame ? { opacity: 0.55, pointerEvents: 'none' } : {}) }}>
        <Typography variant="subtitle2" sx={sectionTitleSx}>
          <PhoneIcon sx={sectionIconSx} />
          Contacto del contratante
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          <TextField
            label="Celular"
            placeholder="912345678"
            value={formData.contractor.phone}
            onChange={(e) => updateContractor({ phone: normalizeChileanPhone(e.target.value) })}
            disabled={isSame}
            size="small"
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="start">+56</InputAdornment>,
                inputProps: { maxLength: 9, inputMode: 'numeric' },
              },
            }}
          />
          <TextField
            label="Email"
            type="email"
            value={formData.contractor.email}
            onChange={(e) => updateContractor({ email: e.target.value })}
            disabled={isSame}
            size="small"
          />
        </Box>
      </Box>

      {/* Address */}
      <Box sx={sectionSx}>
        <Typography variant="subtitle2" sx={sectionTitleSx}>
          <LocationOnIcon sx={sectionIconSx} />
          Dirección
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 2fr' }, gap: 2 }}>
          <FormControl fullWidth error={!!errors.region} size="small">
            <InputLabel>Región</InputLabel>
            <Select
              value={formData.contractor.regionId ?? ''}
              label="Región"
              onChange={(e) => {
                updateContractor({ regionId: Number(e.target.value), communeId: null });
                setErrors((p) => ({ ...p, region: '' }));
              }}
            >
              {regions.map((r) => (
                <MenuItem key={r.int_Id} value={r.int_Id}>
                  {r.str_Nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth error={!!errors.commune} size="small">
            <InputLabel>Comuna</InputLabel>
            <Select
              value={formData.contractor.communeId ?? ''}
              label="Comuna"
              onChange={(e) => {
                updateContractor({ communeId: Number(e.target.value) });
                setErrors((p) => ({ ...p, commune: '' }));
              }}
              disabled={!formData.contractor.regionId}
            >
              {communes.map((c) => (
                <MenuItem key={c.int_Id} value={c.int_Id}>
                  {c.str_Nombre}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Dirección"
            value={formData.contractor.address}
            onChange={(e) => {
              updateContractor({ address: e.target.value });
              setErrors((p) => ({ ...p, address: '' }));
            }}
            error={!!errors.address}
            size="small"
          />
        </Box>
      </Box>

      {/* Insurance config */}
      <Box sx={sectionSx}>
        <Typography variant="subtitle2" sx={sectionTitleSx}>
          <TuneIcon sx={sectionIconSx} />
          Configuración del seguro
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: 2 }}>
          <TextField
            label="Cantidad de cuotas"
            type="number"
            value={formData.insuranceConfig.installmentCount}
            onChange={(e) => updateConfig({ installmentCount: Number(e.target.value) || 1 })}
            size="small"
            slotProps={{ htmlInput: { min: 1, max: 36 } }}
          />
          <FormControl fullWidth size="small">
            <InputLabel>Vigencia</InputLabel>
            <Select
              value={formData.insuranceConfig.validityPeriod}
              label="Vigencia"
              onChange={(e) => updateConfig({ validityPeriod: Number(e.target.value) as 12 | 24 })}
            >
              <MenuItem value={12}>12 meses</MenuItem>
              <MenuItem value={24}>24 meses</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth size="small">
            <InputLabel>Forma de pago</InputLabel>
            <Select
              value={formData.insuranceConfig.paymentMethod}
              label="Forma de pago"
              onChange={(e) => updateConfig({ paymentMethod: Number(e.target.value) as 1 | 2 | 3 | 4 })}
            >
              <MenuItem value={1}>PAC</MenuItem>
              <MenuItem value={2}>PAT</MenuItem>
              <MenuItem value={3}>Contado</MenuItem>
              <MenuItem value={4}>Aviso de Vencimiento</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Snackbar
        open={!!quoteError}
        autoHideDuration={6000}
        onClose={() => setQuoteError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity="warning"
          onClose={() => setQuoteError(null)}
          sx={{
            backgroundColor: NAVY,
            color: '#fff',
            fontFamily: 'Jost, sans-serif',
            fontWeight: 600,
            fontSize: '14px',
            borderRadius: '12px',
            boxShadow: '0px 12px 32px rgba(21, 24, 58, 0.25)',
            backdropFilter: 'blur(12px)',
            '& .MuiAlert-icon': { color: YELLOW },
            '& .MuiAlert-action .MuiIconButton-root': { color: 'rgba(255,255,255,0.7)' },
          }}
        >
          {quoteError}
        </Alert>
      </Snackbar>

      {/* Actions */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 2 }}>
        <Button variant="outlined" onClick={onBack} startIcon={<NavigateBeforeIcon />} sx={backButtonSx}>
          Atrás
        </Button>
        <Button
          variant="contained"
          onClick={() => {
            handleSubmit().catch(() => {});
          }}
          sx={primaryButtonSx}
        >
          Cotizar
        </Button>
      </Box>
    </Box>
  );
};

export default ContractorAndConfigStep;
